import { useCallback, useMemo } from 'react';
import { useFavorites, useToggleFavorite } from './useFavorites';
import {
  useFavoriteGroups,
  useCreateFavoriteGroup,
  useRenameFavoriteGroup,
  useDeleteFavoriteGroup,
  useAddFavoriteToGroup,
  useRemoveFavoriteFromGroup,
  type FavoriteGroup,
} from './useFavoriteGroups';
import { useToast } from '../context/ToastContext';
import { useOfflineStatus } from './useOfflineStatus';
import { useSettingsStore } from '../stores/useSettingsStore';

export type { FavoriteGroup };

export function useFavoriteActions(isLoggedIn: boolean) {
  const { data: favoritesData = [] } = useFavorites(isLoggedIn);
  const { favoriteGroupsEnabled } = useSettingsStore();
  const { data: groupsData = [] } = useFavoriteGroups(isLoggedIn && favoriteGroupsEnabled);
  const toast = useToast();
  const { refreshPendingCount } = useOfflineStatus();

  const toggleFavoriteMutation = useToggleFavorite();
  const createGroupMutation = useCreateFavoriteGroup();
  const renameGroupMutation = useRenameFavoriteGroup();
  const deleteGroupMutation = useDeleteFavoriteGroup();
  const addToGroupMutation = useAddFavoriteToGroup();
  const removeFromGroupMutation = useRemoveFavoriteFromGroup();

  const favorites = useMemo(() => favoritesData.map((f) => String(f.product_id)), [favoritesData]);
  const favoriteGroups = groupsData;

  const toggleFavorite = useCallback((id: string) => {
    return toggleFavoriteMutation.mutateAsync(Number(id), {
      onSuccess: () => refreshPendingCount(),
      onError: () => toast.show('Favorite could not be saved', 'error'),
    });
  }, [toggleFavoriteMutation, toast, refreshPendingCount]);

  const isFavorite = useCallback(
    (id: string) => favorites.includes(id),
    [favorites],
  );

  const createFavoriteGroup = useCallback((name: string) => {
    createGroupMutation.mutate(name);
  }, [createGroupMutation]);

  const deleteFavoriteGroup = useCallback((groupId: number | string) => {
    deleteGroupMutation.mutate(groupId);
  }, [deleteGroupMutation]);

  const renameFavoriteGroup = useCallback((groupId: number | string, name: string) => {
    renameGroupMutation.mutate({ groupId, name });
  }, [renameGroupMutation]);

  const addFavoriteToGroup = useCallback((favoriteId: number | string, groupId: number | string) => {
    addToGroupMutation.mutate({ groupId: groupId as number, favoriteId });
  }, [addToGroupMutation]);

  const removeFavoriteFromGroup = useCallback((favoriteId: number | string, groupId: number | string) => {
    removeFromGroupMutation.mutate({ groupId: groupId as number, favoriteId });
  }, [removeFromGroupMutation]);

  const moveFavoriteToGroup = useCallback((favoriteId: number | string, fromGroupId: number | string, toGroupId: number | string) => {
    removeFromGroupMutation.mutate({ groupId: fromGroupId as number, favoriteId });
    addToGroupMutation.mutate({ groupId: toGroupId as number, favoriteId });
  }, [removeFromGroupMutation, addToGroupMutation]);

  return {
    favorites, toggleFavorite, isFavorite,
    favoriteGroups, createFavoriteGroup, deleteFavoriteGroup, renameFavoriteGroup,
    addFavoriteToGroup, removeFavoriteFromGroup, moveFavoriteToGroup,
  };
}
